// #ux-redesign-start: InboxSearcher — copia 1:1 del de Doctors (sin trash/folder
// branches porque Patients no los soporta). Live-search con debounce 350ms,
// Enter inmediato, Escape clear, dispatch por box.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslate } from 'react-translate';

import {
  actionGetMessages,
  actionOutboxGetMessages,
  actionGetPriorityMessages,
} from 'store/actions/messagesActions';

const DEBOUNCE_MS = 350;

const InboxSearcher = ({ box }) => {
  const t = useTranslate('messages');
  const ta = useTranslate('a11y');
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const lastDispatchedRef = useRef('');
  const [hasText, setHasText] = useState(false);

  const triggerSearch = useCallback(
    (value) => {
      const search = value && value.trim().length > 0 ? value.trim() : null;
      const key = search || '';
      if (lastDispatchedRef.current === key) return;
      lastDispatchedRef.current = key;

      switch (box) {
        case 'outbox':
          dispatch(actionOutboxGetMessages(1, 20, search));
          break;
        case 'priority':
          dispatch(actionGetPriorityMessages(1, 20, search));
          break;
        case 'inbox':
        default:
          dispatch(actionGetMessages(1, 20, search));
          break;
      }
    },
    [box, dispatch]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (inputRef.current) inputRef.current.value = '';
    setHasText(false);
    lastDispatchedRef.current = '';
  }, [box]);

  const scheduleSearch = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      triggerSearch(value);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setHasText(v.length > 0);
    if (v.length === 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      triggerSearch('');
      return;
    }
    scheduleSearch(v);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      triggerSearch(e.target.value);
    } else if (e.key === 'Escape') {
      handleClear();
    }
  };

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      setHasText(false);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      triggerSearch('');
      inputRef.current.focus();
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        id="inbox-searcher"
        type="text"
        className="inbox-searcher-input form-control"
        placeholder={t('searcher')}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {hasText && (
        <button
          type="button"
          className="inbox-searcher-clear"
          onClick={handleClear}
          aria-label={ta('clear-search')}
          title={ta('clear-search')}
        >
          ×
        </button>
      )}
    </>
  );
};

InboxSearcher.propTypes = {
  box: PropTypes.string,
};

export default InboxSearcher;
// #ux-redesign-end
